import type {ILocation} from "@chaosinsight/postoffice-portalclient"
import type PublicUser from "@/managers/data/user/publicUser"
import type ILocationBase from "@/managers/data/location/iLocationBase"
import type IPostOwner from "@/managers/data/post/iPostOwner"
import DealConfiguration from "@/managers/data/organization/dealConfiguration"
import ECommerceIntegrations from "@/managers/data/location/eCommerceIntegrations"
import SubscriptionConfiguration from "@/managers/data/location/subscriptionConfiguration"
import type DealTexts from "@/managers/data/organization/dealTexts"
import {LocationAddress} from "./locationAddress"
import Configuration from "@/managers/system/configuration"

export {ECommerceIntegrations, LocationAddress, SubscriptionConfiguration, DealConfiguration}

export default class Location implements IPostOwner, ILocationBase  {
	public id: number
	public userDefinedId: string | null
	public organizationId: number
	public name: string
	public key: string
	public timeZone: string
	public logoUrl: string
	public language: string

	public lastActivityOn: Date | null
	public userCount: number
	public followers: number

	public isSharingInsightsWithOrganization: boolean
	public isDealsEnabled: boolean
	public isPrioritizingMessaging: boolean | null
	public isLiveDealsEnabled: boolean
	public isLikeCommentOnOrderEnabled: boolean
	public isPickupReminderEnabled: boolean

	public dealConfiguration: DealConfiguration
	public eCommerceIntegrations: ECommerceIntegrations
	public subscriptionConfiguration: SubscriptionConfiguration
	public address: LocationAddress | null

	public users: PublicUser[] | null

	public get dealTexts(): DealTexts {
		return this.dealConfiguration.texts.get(this.language)!
	}

	constructor(
		id: number, userDefinedId: string | null, organizationId: number, key: string, name: string, timeZone: string, logoUrl: string, language: string,
		lastActivityOn: Date | null, userCount: number, followers: number, isSharingInsightsWithOrganization: boolean, isDealsEnabled: boolean,
		isPrioritizingMessaging: boolean | null, isLiveDealsEnabled: boolean, isLikeCommentOnOrderEnabled: boolean, isPickupReminderEnabled: boolean,
		dealConfiguration: DealConfiguration,
		eCommerceIntegrations: ECommerceIntegrations,
		subscriptionConfiguration: SubscriptionConfiguration,
		address: LocationAddress | null,
		users: PublicUser[] | null) {
		this.id = id
		this.userDefinedId = userDefinedId
		this.organizationId = organizationId
		this.name = name
		this.key = key
		this.timeZone = timeZone
		this.logoUrl = logoUrl ?? Configuration.general.fallbackLogo
		this.language = language
		this.lastActivityOn = lastActivityOn
		this.userCount = userCount
		this.followers = followers
		this.isSharingInsightsWithOrganization = isSharingInsightsWithOrganization
		this.isDealsEnabled = isDealsEnabled
		this.isPrioritizingMessaging = isPrioritizingMessaging
		this.isLiveDealsEnabled = isLiveDealsEnabled
		this.isLikeCommentOnOrderEnabled = isLikeCommentOnOrderEnabled
		this.isPickupReminderEnabled = isPickupReminderEnabled
		this.dealConfiguration = dealConfiguration
		this.eCommerceIntegrations = eCommerceIntegrations
		this.subscriptionConfiguration = subscriptionConfiguration
		this.address = address
		this.users = users
	}

	public static fromApi(data: ILocation): Location {
		return new Location(
			data.Id,
			data.UserDefinedId,
			data.OrganizationId,
			data.Key,
			data.Name,
			data.TimeZone,
			data.LogoUrl,
			data.Language,
			data.Statistics.LastActivityOn !== null ? new Date(data.Statistics.LastActivityOn) : null,
			data.Statistics.UserCount,
			data.Statistics.Followers,
			data.Statistics.IsShareInsightsWithOrganizationEnabled,
			data.Deals.Regular.IsEnabled,
			data.Deals.Common.IsMessagingPrioritized,
			data.Deals.Live.IsEnabled,
			data.Deals.Common.IsLikeCommentOnOrderEnabled,
			data.Deals.Common.IsPickupReminderEnabled,
			DealConfiguration.fromApi(data.Deals, data.Language),
			ECommerceIntegrations.fromApi(data.ECommerceIntegrations),
			SubscriptionConfiguration.fromApi(data.SalesSubscription),
			LocationAddress.fromApi(data.Address),
			null)
	}
}
